import { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2 } from '../../lib/icons';
import { fmt } from '../shared/badges';
import { CATEGORIES_INDUSTRIELLES, LIGNES_PRODUCTION, creerVersionStandard, supprimerStandard } from '../../lib/cri';
import { construireStandard } from '../../lib/criCalculs';
import { Carte, btn, btnLight, inputCls, labelCls, td, th } from './ui';

const vide = () => CATEGORIES_INDUSTRIELLES.map((c) => ({ categorie: c, variableTndParTonne: '', fixeTnd: '' }));

export const StandardsCri = ({ standards, campagne, utilisateur, onMaj }) => {
  const [ligne, setLigne] = useState(LIGNES_PRODUCTION[0]);
  const [applicableDu, setApplicableDu] = useState(new Date().toISOString().slice(0, 10));
  const [capacite, setCapacite] = useState('');
  const [notes, setNotes] = useState('');
  const [cats, setCats] = useState(vide);

  const apercu = construireStandard({
    categories: cats.map((c) => ({
      categorie: c.categorie,
      variableTndParTonne: Number(c.variableTndParTonne) || 0,
      fixeTnd: Number(c.fixeTnd) || 0,
    })),
    capaciteNormaleT: Number(capacite) || 0,
  });

  const enregistrer = async () => {
    const { error } = await creerVersionStandard({
      campagne,
      ligne,
      applicableDu,
      capaciteNormaleT: Number(capacite) || 0,
      categories: cats.map((c) => ({
        categorie: c.categorie,
        variableTndParTonne: Number(c.variableTndParTonne) || 0,
        fixeTnd: Number(c.fixeTnd) || 0,
      })),
      notes,
      utilisateur,
    });
    if (error) return toast.error(error);
    toast.success('Nouvelle version du standard enregistrée — applicable aux productions futures.');
    setCats(vide());
    setNotes('');
    onMaj();
  };

  const supprimer = async (id) => {
    const { error } = await supprimerStandard(id, utilisateur);
    if (error) return toast.error(error);
    toast.success('Version supprimée.');
    onMaj();
  };

  return (
    <div className="space-y-4">
      <Carte
        testid="cri-standard-form"
        titre="Nouvelle version du standard industriel"
        sous="Coûts variables prévus par tonne + charges fixes rapportées à la capacité normale. Les taux sont nets de TVA récupérable : la TVA non récupérable est déjà supportée dans le coût."
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="block">
            <span className={labelCls}>Ligne de production</span>
            <select data-testid="cri-standard-ligne" value={ligne} onChange={(e) => setLigne(e.target.value)} className={inputCls}>
              {LIGNES_PRODUCTION.map((l) => (
                <option key={l}>{l}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className={labelCls}>Applicable à partir du</span>
            <input data-testid="cri-standard-date" type="date" value={applicableDu} onChange={(e) => setApplicableDu(e.target.value)} className={inputCls} />
          </label>
          <label className="block">
            <span className={labelCls}>Capacité normale (tonnes)</span>
            <input data-testid="cri-standard-capacite" type="number" step="0.001" value={capacite} onChange={(e) => setCapacite(e.target.value)} className={inputCls} placeholder="ex. 15" />
          </label>
          <label className="block">
            <span className={labelCls}>Note interne</span>
            <input data-testid="cri-standard-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className={inputCls} placeholder="Base de calcul, hypothèses…" />
          </label>
        </div>

        <div className="mt-4 overflow-x-auto border border-slate-200">
          <table className="w-full min-w-[640px] border-collapse">
            <thead className="bg-slate-50">
              <tr>
                <th className={th}>Catégorie industrielle</th>
                <th className={th}>Variable (TND/t)</th>
                <th className={th}>Charges fixes (TND)</th>
                <th className={th}>Fixe réparti (TND/t)</th>
                <th className={th}>Taux standard (TND/t)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {cats.map((c, i) => {
                const l = apercu.categories.find((x) => x.categorie === c.categorie);
                return (
                  <tr key={c.categorie}>
                    <td className={td}>{c.categorie}</td>
                    <td className="px-2 py-1">
                      <input
                        data-testid={`cri-standard-variable-${i}`}
                        type="number"
                        step="0.001"
                        value={c.variableTndParTonne}
                        onChange={(e) => setCats((p) => p.map((x, j) => (j === i ? { ...x, variableTndParTonne: e.target.value } : x)))}
                        className="h-9 w-32 border border-slate-300 bg-slate-50 px-2 font-data text-sm outline-none focus:border-slate-900 focus:bg-white"
                      />
                    </td>
                    <td className="px-2 py-1">
                      <input
                        data-testid={`cri-standard-fixe-${i}`}
                        type="number"
                        step="0.001"
                        value={c.fixeTnd}
                        onChange={(e) => setCats((p) => p.map((x, j) => (j === i ? { ...x, fixeTnd: e.target.value } : x)))}
                        className="h-9 w-32 border border-slate-300 bg-slate-50 px-2 font-data text-sm outline-none focus:border-slate-900 focus:bg-white"
                      />
                    </td>
                    <td className={`${td} text-slate-600`}>{fmt(l?.fixeTndParTonne ?? 0, 2)}</td>
                    <td className={`${td} font-semibold`}>{fmt(l?.tauxTndParTonne ?? 0, 2)}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-slate-50">
              <tr>
                <td className={`${td} font-bold`}>Total standard</td>
                <td className={td} colSpan={3} />
                <td data-testid="cri-standard-total" className={`${td} font-bold`}>
                  {fmt(apercu.tauxTndParTonne, 2)} TND/t
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        <button data-testid="cri-standard-submit" type="button" onClick={enregistrer} className={`${btn} mt-4`}>
          <Plus size={15} /> Enregistrer la version
        </button>
      </Carte>

      <Carte testid="cri-standard-liste" titre="Versions enregistrées" sous="Une nouvelle version ne s’applique qu’aux productions futures : les OF déjà figés conservent la leur.">
        {standards.length === 0 ? (
          <p className="text-sm text-slate-500">Aucun standard enregistré pour l’instant.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] border-collapse">
              <thead className="bg-slate-50">
                <tr>
                  <th className={th}>Campagne</th>
                  <th className={th}>Ligne</th>
                  <th className={th}>Version</th>
                  <th className={th}>Applicable du</th>
                  <th className={th}>Capacité normale</th>
                  <th className={th}>Détail</th>
                  <th className={th}>Taux (TND/t)</th>
                  <th className={th} />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {standards.map((s) => (
                  <tr key={s.id} data-testid={`cri-standard-row-${s.id}`}>
                    <td className={td}>{s.campagne}</td>
                    <td className={td}>{s.ligne}</td>
                    <td className={td}>v{s.version}</td>
                    <td className={td}>{s.applicableDu}</td>
                    <td className={td}>{s.capaciteNormaleT ? `${fmt(s.capaciteNormaleT, 2)} t` : '—'}</td>
                    <td className="px-3 py-2 text-[11px] text-slate-600">
                      {s.categories.map((c) => `${c.categorie} ${fmt(c.tauxTndParTonne, 2)}`).join(' · ')}
                    </td>
                    <td className={`${td} font-semibold`}>{fmt(s.tauxTndParTonne, 2)}</td>
                    <td className="px-3 py-2">
                      <button data-testid={`cri-standard-del-${s.id}`} type="button" onClick={() => supprimer(s.id)} className={btnLight}>
                        <Trash2 size={13} /> Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Carte>
    </div>
  );
};
